import type { Directive } from 'vue'
import { getQuery } from 'ufo'
import { navigateTo, getTermsLinkQuery, TERMS_KEYS, PROMOTION_PARAMS } from '#imports'

const resolveInternalUrl = (href: string): URL | undefined => {
    let url: URL

    try {
        url = new URL(href, window.location.href)
    }
    catch {
        return
    }

    if (url.origin !== window.location.origin) return

    return url
}

/**
 * Vue directive that converts internal anchor tags within an element to behave like NuxtLinks.
 * It prevents default link behavior for internal routing,
 * also merging links to the special terms into the current query.
 * Hrefs are resolved against the current origin instead of matched by prefix, because third-party
 * scripts such as the GA4 cross-domain linker rewrite internal hrefs to absolute, decorated URLs.
 */
export const vInternalLinks: Directive = {
    mounted(el: HTMLElement) {
        const handleClick = async (event: Event) => {
            const anchor = (event.target as HTMLElement).closest('a') as HTMLAnchorElement

            if (!anchor) return

            const href = anchor.getAttribute('href')

            if (!href) return

            const url = resolveInternalUrl(href)

            if (!url) return

            event.preventDefault()

            const currentQuery = getQuery(window.location.href)
            const query = getTermsLinkQuery(url.href, window.location.href, currentQuery)

            if (!query) return await navigateTo(`${url.pathname}${url.search}${url.hash}`)

            // Replacing the open terms keeps the page without terms one entry back, which closing the dialog goes back to.
            await navigateTo({ query }, { replace: [...TERMS_KEYS, ...PROMOTION_PARAMS].some(key => key in currentQuery) })
        }

        // Store the handler for cleanup
        (el as any)._linksHandler = handleClick

        el.addEventListener('click', handleClick)
    },

    unmounted(el: HTMLElement) {
        const handler = (el as any)._linksHandler

        if (handler) {
            el.removeEventListener('click', handler)

            delete (el as any)._linksHandler
        }
    }
}
